// File: frontend/src/context/SiteSettingsContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

// Default values that match the seed data — shown until the API responds
const DEFAULTS = {
    siteName:               "3 Korner 1 Penaltı",
    siteSlogan:             "Vurduğun gol, yediğin ofsayt olsun!",
    maintenanceEnabled:     false,
    announcementEnabled:    false,
    announcementText:       "",
    // Search defaults mirror SettingConstants.java and createtables.sql seed values
    searchMinChars:         3,
    searchDelayMs:          300,
    // Entry display defaults mirror SettingConstants.java and createtables.sql seed values
    entryPreviewLineLimit:  10,
    // Entry length defaults mirror SettingConstants.java seed values; the real
    // values arrive from /api/settings/site and drive every editor's counter
    entryMinLength:         10,
    entryMaxLength:         4000,
    // Username max length default mirrors SettingConstants.USERNAME_MAX_LENGTH (hard ceiling 50)
    usernameMaxLength:      50,
    // Topic title max length default mirrors the seed value (allowed range 50-80)
    topicTitleMaxLength:    80,
    // Private message max length default mirrors the seed value of messaging.max_length.
    // The real value arrives from /api/settings/site and drives the composer's counter.
    messageMaxLength:       2000,
    // Cookie bar is on by default — a visitor must be asked before anything optional runs
    cookieBannerEnabled:    true,
    // Minimum registration age; mirrors SettingConstants.LEGAL_MIN_AGE_DEFAULT
    legalMinAge:            18,
};

interface SiteSettingsContextType {
    siteName:               string;
    siteSlogan:             string;
    maintenanceEnabled:     boolean;
    announcementEnabled:    boolean;
    announcementText:       string;
    /** Minimum characters before typeahead fires (search.typeahead_min_chars) */
    searchMinChars:         number;
    /** Debounce delay in ms before typeahead fires (search.typeahead_delay_ms) */
    searchDelayMs:          number;
    /** Lines shown before "devamını gör" link appears; 0 = no truncation (entry.preview_line_limit) */
    entryPreviewLineLimit:  number;
    /** Admin-configurable minimum entry length in characters (entry.min_length) */
    entryMinLength:         number;
    /** Admin-configurable maximum entry length in characters (entry.max_length) */
    entryMaxLength:         number;
    /** Admin-configurable username max length (user.username.max_length, hard ceiling 50) */
    usernameMaxLength:      number;
    /** Admin-configurable custom topic title max length (topic.title.max_length, range 50-80) */
    topicTitleMaxLength:    number;
    /** Admin-configurable private message max length (messaging.max_length) */
    messageMaxLength:       number;
    /** Whether the cookie consent bar is shown (legal.cookie_banner.enabled) */
    cookieBannerEnabled:    boolean;
    /** Minimum age required to register, shown on the consent line (legal.min_age) */
    legalMinAge:            number;
    /** Call this after the admin saves settings so the header updates immediately */
    refresh: () => Promise<void>;
}

const SiteSettingsContext = createContext<SiteSettingsContextType>({
    ...DEFAULTS,
    refresh: async () => {},
});

interface SiteSettingsProviderProps {
    children: ReactNode;
}

export const SiteSettingsProvider: React.FC<SiteSettingsProviderProps> = ({ children }) => {
    const [siteName,              setSiteName]              = useState(DEFAULTS.siteName);
    const [siteSlogan,            setSiteSlogan]            = useState(DEFAULTS.siteSlogan);
    const [maintenanceEnabled,    setMaintenanceEnabled]    = useState(DEFAULTS.maintenanceEnabled);
    const [announcementEnabled,   setAnnouncementEnabled]   = useState(DEFAULTS.announcementEnabled);
    const [announcementText,      setAnnouncementText]      = useState(DEFAULTS.announcementText);
    // Search settings read from the backend so the SearchBar respects admin-configured values
    const [searchMinChars,        setSearchMinChars]        = useState(DEFAULTS.searchMinChars);
    const [searchDelayMs,         setSearchDelayMs]         = useState(DEFAULTS.searchDelayMs);
    // Entry display setting — controls "devamını gör" truncation threshold
    const [entryPreviewLineLimit, setEntryPreviewLineLimit] = useState(DEFAULTS.entryPreviewLineLimit);
    // Entry length rules — read by EntryPostForm, EntryRow, DraftsPage and the
    // admin entry modals so their counters follow the admin panel value
    const [entryMinLength,        setEntryMinLength]        = useState(DEFAULTS.entryMinLength);
    const [entryMaxLength,        setEntryMaxLength]        = useState(DEFAULTS.entryMaxLength);
    // Username max length — read by RegisterModal and AdminUsers so the client-side
    // limit matches the admin-configured value
    const [usernameMaxLength,     setUsernameMaxLength]     = useState(DEFAULTS.usernameMaxLength);
    // Topic title max length — read by SuggestCustomTopicModal for maxLength + dynamic placeholder
    const [topicTitleMaxLength,   setTopicTitleMaxLength]   = useState(DEFAULTS.topicTitleMaxLength);
    // Message max length — read by the conversation composer so its counter follows
    // the admin panel instead of a compiled-in number
    const [messageMaxLength,      setMessageMaxLength]      = useState(DEFAULTS.messageMaxLength);
    // Legal settings — read by CookieConsentBanner and by the registration consent line
    const [cookieBannerEnabled,   setCookieBannerEnabled]   = useState(DEFAULTS.cookieBannerEnabled);
    const [legalMinAge,           setLegalMinAge]           = useState(DEFAULTS.legalMinAge);

    // Fetch from the public backend endpoint and apply to state
    const fetchSettings = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/settings/site`);
            if (!response.ok) return; // Keep defaults silently on error

            const data: Record<string, string> = await response.json();

            if (data["site.name"])                  setSiteName(data["site.name"]);
            if (data["site.slogan"] !== undefined)   setSiteSlogan(data["site.slogan"]);
            if (data["site.maintenance.enabled"])    setMaintenanceEnabled(data["site.maintenance.enabled"] === "true");
            if (data["site.announcement.enabled"])   setAnnouncementEnabled(data["site.announcement.enabled"] === "true");
            if (data["site.announcement.text"] !== undefined) setAnnouncementText(data["site.announcement.text"]);

            // Parse search settings; fall back to defaults if missing or non-numeric
            const minChars = parseInt(data["search.typeahead_min_chars"], 10);
            if (!isNaN(minChars) && minChars > 0) setSearchMinChars(minChars);

            const delayMs = parseInt(data["search.typeahead_delay_ms"], 10);
            if (!isNaN(delayMs) && delayMs >= 0) setSearchDelayMs(delayMs);

            // Parse entry preview line limit; 0 means no truncation
            const lineLimit = parseInt(data["entry.preview_line_limit"], 10);
            if (!isNaN(lineLimit) && lineLimit >= 0) setEntryPreviewLineLimit(lineLimit);

            // Parse entry length rules; both are clamped/validated on the backend
            const entryMin = parseInt(data["entry.min_length"], 10);
            if (!isNaN(entryMin) && entryMin > 0) setEntryMinLength(entryMin);

            const entryMax = parseInt(data["entry.max_length"], 10);
            if (!isNaN(entryMax) && entryMax > 0) setEntryMaxLength(entryMax);

            // Parse username max length; backend already clamps to the hard ceiling of 50
            const unameMax = parseInt(data["user.username.max_length"], 10);
            if (!isNaN(unameMax) && unameMax > 0) setUsernameMaxLength(unameMax);

            // Parse topic title max length; backend already clamps to [50, 80]
            const topicMax = parseInt(data["topic.title.max_length"], 10);
            if (!isNaN(topicMax) && topicMax > 0) setTopicTitleMaxLength(topicMax);

            // Parse private message max length; backend validates it against the
            // column width before storing, so anything arriving here is safe to use
            const messageMax = parseInt(data["messaging.max_length"], 10);
            if (!isNaN(messageMax) && messageMax > 0) setMessageMaxLength(messageMax);

            // Cookie bar visibility — only an explicit "false" turns it off, so a
            // missing or malformed value keeps asking for consent rather than
            // silently skipping it
            if (data["legal.cookie_banner.enabled"] !== undefined) {
                setCookieBannerEnabled(data["legal.cookie_banner.enabled"] !== "false");
            }

            // Minimum registration age; backend already clamps to a sane range
            const minAge = parseInt(data["legal.min_age"], 10);
            if (!isNaN(minAge) && minAge > 0) setLegalMinAge(minAge);
        } catch {
            // Network error — keep defaults, do not crash the app
        }
    };

    // Load on mount
    useEffect(() => {
        fetchSettings();
    }, []);

    return (
        <SiteSettingsContext.Provider value={{
            siteName,
            siteSlogan,
            maintenanceEnabled,
            announcementEnabled,
            announcementText,
            searchMinChars,
            searchDelayMs,
            entryPreviewLineLimit,
            entryMinLength,
            entryMaxLength,
            usernameMaxLength,
            topicTitleMaxLength,
            messageMaxLength,
            cookieBannerEnabled,
            legalMinAge,
            refresh: fetchSettings,
        }}>
            {children}
        </SiteSettingsContext.Provider>
    );
};

/**
 * Hook to consume site settings anywhere in the component tree.
 * The header uses this for site name and slogan.
 * The SearchBar uses searchMinChars and searchDelayMs.
 * EntryRow uses entryPreviewLineLimit for "devamını gör" truncation.
 * Every entry editor uses entryMinLength / entryMaxLength for its character counter.
 * The conversation composer uses messageMaxLength for the same reason.
 * AdminSettings calls refresh() after a successful save.
 */
export const useSiteSettings = (): SiteSettingsContextType => {
    return useContext(SiteSettingsContext);
};